// projectConfig.js
// TCC-specific project configuration.
// All content here is project-specific and can be replaced for a new project.
// Platform infrastructure (App.js, StatusTab, etc.) imports from here.

export const NAV = [
  "Status",
  "AI Summit",
  "AI Charter",
  "AI Business Case",
  "Data Audit",
  "Architecture",
  "AI Strategy",
  "Agentic Day Planner",
  "Ownership",
  "Metrics",
  "Readiness Capture",
  "Use Case Capture",
  "Data Platform Capture",
  "Pilot Planning Capture",
  "Governance Capture",
  "Story Capture",
  "Execution Capture",
  "Current State Capture",
  "Day 1 Wrap Capture",
  "Summit Close Capture",
  "Acceleration Capture",
  "New Hire Onboarding Tracker",
  "SMS Notifications",
  "Admin",
];
export const TASKS = [
  {
    track: "Data and Systems",
    items: [
      { task: "Finalize Data Source Tracker - single owner per system", status: "not-started", risk: false, note: "Ownership gaps remain: ChatGPT, VaaS Library, Mercury." },
      { task: "Define metric definitions: Revenue, Spread, Starts, Fill Rate, Ks On, Billables", status: "in-progress", risk: true, note: "Started. Active conflicts unresolved. Blocking dashboard builds." },
      { task: "Audit and clean Bullhorn - duplicates, ownership rules, employment type", status: "in-progress", risk: true, note: "Started. Duplicates identified. Not resolved." },
      { task: "Replace CData Connect AI - no raw system access to AI", status: "in-progress", risk: true, note: "Started. Raw Bullhorn-to-ChatGPT pipe still active. P0 risk." },
      { task: "Stand up Raw Landing Zone - immutable source copies", status: "not-started", risk: false, note: "Blocked by ETL tool selection." },
    ],
  },
  {
    track: "Reporting",
    items: [
      { task: "Clean up Bullhorn Analytics pages", status: "in-progress", risk: false, note: "Started. Not complete." },
      { task: "Define page views per stakeholder role", status: "in-progress", risk: false, note: "In progress alongside Bullhorn Analytics cleanup." },
      { task: "Freeze Monthly Ops Reporting - document current logic", status: "not-started", risk: true, note: "Not started. Logic changes risk making future migration harder." },
      { task: "Map all active Excel reports - migrate vs retire vs replace", status: "not-started", risk: false, note: "Tracker has inventory. Actions not assigned." },
      { task: "Build role-based dashboard specs: Recruiter, Rep, SAE, Site Lead, Executive", status: "not-started", risk: false, note: "Blocked by metric definitions." },
    ],
  },
  {
    track: "Governance",
    items: [
      { task: "Assign data domain owners per system", status: "in-progress", risk: false, note: "Tracker populated. TBD slots remain." },
      { task: "Establish weekly Data Intelligence sync cadence", status: "in-progress", risk: false, note: "Started. Not fully established." },
      { task: "Document conflict resolution rule: NetSuite wins on financials, Bullhorn wins on ops", status: "not-started", risk: true, note: "Not written. Active metric conflicts need this now." },
    ],
  },
  {
    track: "AI Summit Prep (June)",
    items: [
      { task: "Confirm attendees", status: "in-progress", risk: false, note: "Started. Not fully confirmed." },
      { task: "Build 2-day session agenda", status: "in-progress", risk: false, note: "Built in AI Summit tab. Needs review and distribution." },
      { task: "Prepare pre-read materials", status: "in-progress", risk: false, note: "AI for Business Primer and AI Readiness tabs serve as pre-read." },
      { task: "Define AI framework and governing principles", status: "in-progress", risk: false, note: "Outlined in AI Strategy tab. Needs executive alignment at summit." },
      { task: "Select ETL and warehouse tooling", status: "in-progress", risk: true, note: "Evaluation started. No selection made. Blocks Phase 2." },
      { task: "Draft AI governance guardrails", status: "not-started", risk: false, note: "Required output from Day 2 working session." },
    ],
  },
];
export const GUIDING_PRINCIPLES = {
  doing: [
    "Increase speed and quality of customer solutions",
    "Improve front and back-office efficiency - do more with the same team",
    "Automate repetitive, low-value tasks",
    "Free staff for more time with candidates and clients",
  ],
  notDoing: [
    "Reduce headcount - AI is an enabler not a displacement signal",
    "Automate direct interactions with candidates or clients",
    "Replace human judgment in high-stakes decisions",
    "Deploy agents without oversight or accountability",
  ],
  principle: "Just because we can does not mean we should.",
};
export const SUCCESS_TARGETS = [
  { metric: "Revenue Growth", target: "40% by July 1, 2027", detail: "Achieved without adding production or back-office headcount. AI absorbs the capacity requirement.", color: "#27ae60" },
  { metric: "No low-value headcount adds", target: "Minimize HC Growth, Maximize Productivity", detail: "Same team, structurally larger business — growth funded by agent-enabled productivity, not added labor.", color: "#2980b9" },
  { metric: "Agent Coverage", target: "90%+ of repeatable workflows", detail: "Every high-volume, low-judgment task in recruiting ops and back office has an agent handling it.", color: "#8e44ad" },
  { metric: "Production Agents Live", target: "5 by July 1, 2027", detail: "5 production agents deployed, monitored, and in active use across the business.", color: "#c9a84c" },
  { metric: "Submittals per Recruiter", target: "2x current rate", detail: "Without adding recruiters. AI-assisted sourcing and screening drives the increase.", color: "#27ae60" },
  { metric: "Time-to-Submit", target: "40% reduction", detail: "Through AI-assisted sourcing and screening.", color: "#2980b9" },
  { metric: "Staff AI Proficiency", target: "100% trained", detail: "100% of production and back-office staff trained before agents go live in their workflow.", color: "#e67e22" },
];
export const CHANGE_MGMT_STEPS = [
  { num: "01", title: "Impact Assessment", desc: "Before design begins: map which roles, workflows, and people are affected. Quantify change magnitude. Identify resistors and champions." },
  { num: "02", title: "Stakeholder Communication", desc: "Communicate scope, timeline, and what this means for each person to every affected team before development starts. Not during. Not after." },
  { num: "03", title: "Training Design", desc: "Build role-specific training curriculum. Set a completion requirement and deadline. Competency is confirmed before access to agent output is required." },
  { num: "04", title: "Pilot with Champions", desc: "Recruit 2-3 staff volunteers to test with. Capture their feedback formally. Champions become internal advocates and first-line support post-launch." },
  { num: "05", title: "Broad Rollout", desc: "Phased by team or function where possible. Leadership check-ins weekly during first 30 days. Escalation path clearly defined." },
  { num: "06", title: "Post-Launch Review", desc: "At 30, 60, and 90 days: survey adoption, measure KPIs vs CBA model, collect qualitative feedback. Publish summary to the full team. Close the loop." },
];
export const RESISTANCE_PATTERNS = [
  { pattern: '"This will replace me"', response: "Address directly and early. Charter language is explicit: we are not reducing headcount. Reinforce in 1:1s and team meetings - once is not enough." },
  { pattern: '"I do not trust what it produces"', response: "Expected and healthy. HITL design means humans verify outputs. Trust is built through exposure not mandate. Start with low-stakes use cases." },
  { pattern: '"I am too busy to learn this"', response: "Training must fit into workflow not sit outside it. Micro-learning, role-specific sessions, and peer coaching reduce the perceived burden." },
  { pattern: '"It does not work for my situations"', response: "Capture the edge cases. Log them. Feed them back into prompt improvement and agent redesign. Front-line friction is product data." },
];
export const SUMMIT_SESSIONS = {
  day1: [
{
      num: 1, time: "8:45 - 9:30", type: "exec",
      block: "Welcome, Kickoff, Guiding Principles and Success Definition",
      sessionNum: "",
      owner: "Dave Smith / Sab Guerriero / Dan Frasco",
      purpose: "Set the stage. Establish guiding principles. Ratify specific success targets. Align on what this summit must produce.",
      items: [
        "Triple Crown at the inflection point - $200M to $500M in 20 months",
        "GUIDING PRINCIPLES: Why we are doing this and why we are NOT",
        "We ARE doing this to: increase speed and quality, improve efficiency, automate low-value tasks, free staff for candidates and clients",
        "We are NOT doing this to: reduce headcount, automate direct candidate/client interactions, replace human judgment, deploy without oversight",
        "RATIFY SUCCESS TARGETS: 40% revenue growth by July 2027 without adding production headcount, 90%+ agent coverage of repeatable workflows, 5 production agents live by July 2027",
        "How we validate success: quarterly governance review, 30-day pilot check, 90-day CBA actuals vs model",
        "SUCCESS CHECKPOINT: define what this 2-day summit must produce",
        "Ground rules: candor over comfort, decisions over discussion",
      ],
      output: "Guiding principles ratified. Specific success targets agreed. Summit outputs defined.",
      presFile: "AI_for_Business_Foundation.pptx",
      facFile: "AI_for_Business_Facilitator_Guide.docx",
      captureTab: null,
      preRead: ["Review TCC AI Charter slides 3-5 before attending", "Review Status tab Phase 1 progress before attending"],
    },

    {
      num: 2, time: "9:30 - 11:00", type: "education",
      block: "AI for Business - Foundation Session",
      sessionNum: "",
      owner: "Dan Frasco",
      purpose: "Establish common language. Every person in the room speaks AI fluently by the end of this session.",
      items: [
        "What AI is and what it is not - plain language definitions",
        "The AI spectrum: automation, machine learning, generative AI, agentic AI",
        "How AI creates business value - the 4 categories: speed, scale, prediction, personalization",
        "What AI cannot do without clean, governed data",
        "Responsible AI: bias, transparency, auditability, human-in-the-loop",
        "AI in staffing and talent: where the industry is heading",
        "Triple Crown AI vision - where we are going and why",
      ],
      output: "Common vocabulary. Everyone can articulate what AI is, what it needs, and what it will do for Triple Crown.",
      presFile: "AI_for_Business_Foundation.pptx",
      facFile: "AI_for_Business_Facilitator_Guide.docx",
      captureTab: null,
      preRead: ["No pre-read required - this IS the foundation session"],
    },
    {
      num: 3, time: "11:00 - 11:15", type: "break",
      block: "Break", owner: "", purpose: "", items: [], output: "", captureTab: null,
    },
    {
      num: 4, time: "11:15 - 12:00", type: "working",
      block: "Current State - Honest Assessment",
      sessionNum: "",
      owner: "Vanessa Fucciani / Martin Lopez",
      purpose: "No sugarcoating. Show the room where we actually are.",
      items: [
        "Data Source Audit - 29 systems, 50+ Excel files, trust levels across the board",
        "Active blockers: metric conflicts, ungoverned AI access, ETL not selected",
        "CData / ChatGPT direct access - the risk we are living with today",
        "Phase 1 progress - what is started, what is not",
        "The gap between current state and AI readiness",
      ],
      output: "Shared current state. No one leaves this session surprised by what Phase 2 requires.",
      presFile: "Current_State_Assessment.pptx",
      facFile: "Current_State_Facilitator_Guide.docx",
      captureTab: "Current State Capture",
      preRead: ["Review Data Audit tab before attending", "Review 30-Day Tracker tab before attending"],
    },
    {
      num: 5, time: "12:00 - 12:30", type: "break",
      block: "Lunch", owner: "", purpose: "", items: [], output: "", captureTab: null,
    },

    {
      num: 6, time: "12:30 - 1:55", type: "working",
      block: "AI Readiness Assessment - Triple Crown",
      sessionNum: "",
      owner: "Martin Lopez",
      purpose: "Score Triple Crown across all 6 readiness dimensions. Identify the real gaps. Assign owners to every gap.",
      items: [
        "Strategy and Leadership: AI vision, executive ownership, budget alignment",
        "Data Readiness: access to required data, governance framework, pipeline maturity",
        "Technology and Infrastructure: stack capability, cloud alignment, MLOps readiness",
        "Organisational Capability: skills inventory, culture of experimentation",
        "Governance and Ethics: AI governance framework, bias, auditability, compliance",
        "Use-Case and Value Delivery: high-impact use cases, KPIs defined, change management",
        "Change Management dimension: who is affected, adoption strategy, communication plan",
      ],
      output: "Completed AI Readiness scorecard. Heat map of gaps. Every gap has an owner and a phase assignment.",
      presFile: "AI_Readiness_Assessment.pptx",
      facFile: "AI_Readiness_Facilitator_Guide.docx",
      captureTab: "Readiness Capture",
      preRead: ["Open Readiness Capture tab - you will score live during this session"],
    },
    {
      num: 7, time: "1:55 - 2:10", type: "break",
      block: "Break", owner: "", purpose: "", items: [], output: "", captureTab: null,
    },
    {
      num: 8, time: "2:10 - 3:25", type: "working",
      block: "Data Platform Working Session",
      sessionNum: "",
      owner: "Martin Lopez",
      purpose: "Make the tooling decisions that have been blocking Phase 2.",
      items: [
        "ETL selection: Fivetran vs Airbyte vs Azure Data Factory - decision criteria and recommendation",
        "Warehouse selection: Snowflake vs BigQuery vs Microsoft Fabric - align on direction",
        "BI tool: PowerBI vs AWS QuickSight vs other - confirm or decide",
        "Semantic layer tooling: dbt Semantic Layer vs Cube vs equivalent",
        "Integration with Microsoft stack: Power Automate, SharePoint, Teams",
        "Total cost of ownership and timeline per option",
      ],
      output: "ETL tool selected. Warehouse direction confirmed. BI tool decision made. All decisions recorded in S6 capture tab.",
      presFile: "Data_Platform_Working_Session.pptx",
      facFile: "Data_Platform_Facilitator_Guide.docx",
      captureTab: "Data Platform Capture",
      preRead: ["Review Architecture tab before attending"],
    },

    {
      num: 9, time: "3:25 - 4:40", type: "working",
      block: "AI Use Case Prioritization",
      sessionNum: "",
      owner: "All",
      purpose: "Identify and rank AI use cases by business value. Confirm the Agentic Day Planner as the Dec 2026 flagship pilot.",
      items: [
        "PROPOSED FLAGSHIP PILOT: Agentic Day Planner - overnight build, Outlook delivery. Summit confirms or adjusts.",
        "Pilot cohort decision: recruiter or sales rep - determined by Bullhorn data quality audit",
        "Recruiter tools: candidate matching, DB quality scoring, pipeline prediction",
        "Rep and SAE tools: client expansion signals, account penetration, proposal support",
        "Executive tools: revenue forecasting, EBITDA trajectory, retention risk scoring",
        "Operations: replace manual Excel reporting with AI-assisted warehouse-backed summaries",
        "Prioritization framework: score each by business value, data readiness, and build complexity",
      ],
      output: "Agentic Day Planner confirmed as Dec 2026 pilot. Full use case list ranked. Top 3 Phase 4 workstreams confirmed.",
      presFile: "AI_Use_Case_Prioritization.pptx",
      facFile: "AI_Use_Case_Prioritization_Facilitator_Guide.docx",
      captureTab: "Use Case Capture",
      preRead: ["Open Use Case Capture tab - you will score live during this session"],
    },
    {
      num: 10, time: "4:40 - 4:55", type: "exec",
      block: "Day 1 Wrap, Decisions and Success Checkpoint",
      sessionNum: "",
      owner: "Dan Frasco / Dave Smith",
      purpose: "Lock Day 1 decisions. Check against success criteria defined this morning. Set up Day 2.",
      items: [
        "Read back all decisions made today",
        "SUCCESS CHECKPOINT: Are we on track against this morning's success definition?",
        "Open items with owners and due dates",
        "Preview Day 2 agenda",
        "Pre-work for Day 2: review AI governance framework draft overnight",
      ],
      output: "Day 1 decision log. Success checkpoint completed. Team prepared for Day 2.",
      presFile: "Day1_Wrap_Preview.pptx",
      facFile: "Day1_Wrap_Facilitator_Guide.docx",
      captureTab: "Day 1 Wrap Capture",
      preRead: null,
    },
    {
      num: 11, time: "5:45 - 8:00 PM", type: "social",
      block: "Team Dinner",
      sessionNum: "",
      owner: "Rachel Hopper",
      purpose: "Team dinner. Full group. Celebrate Day 1 progress.",
      items: [
        "Bulevar Mexican Kitchen",
        "9400 Arboretum Blvd, Austin, TX 78759",
      ],
      output: "",
      presFile: null,
      facFile: null,
      captureTab: null,
      preRead: null,
    },
  ],
  day2: [
    {
      num: 1, time: "Pre-Summit", type: "exec",
      block: "Leadership Alignment - Pre-Summit Discussion",
      sessionNum: "",
      owner: "Dave Smith / Sab Guerriero / Dan Frasco / Rachel Hopper",
      purpose: "Private conversation before the full group convenes. 30 minutes. Not a full group agenda item.",
      items: [
        "As the Data and AI program scales past Phase 2, who owns program execution and cross-functional alignment?",
        "Current model: Dave and Sab own vision, Dan owns architecture, AI strategy and execution",
        "Open question: is there a dedicated program execution owner as this grows to $500M scale?",
        "Rachel consideration: Head of People role includes cross-functional alignment - relevant to program cadence and accountability",
        "Future hire consideration: CDO or VP of Data and AI is the right answer at $500M scale",
        "Decision options: Dan carries execution for now, future hire named as a planned decision",
        "This does not need to be decided at the summit - but naming it here prevents it becoming a gap later",
      ],
      output: "Alignment on who carries program execution through Phase 2. Decision on whether a future hire is named at the summit.",
      presFile: null,
      facFile: null,
      captureTab: null,
      preRead: null,
    },
    {
      num: 2, time: "8:45 - 9:15", type: "exec",
      block: "Day 2 Opening and Success Checkpoint",
      sessionNum: "",
      owner: "Dave Smith / Dan Frasco",
      purpose: "Recap Day 1. Review success criteria. Confirm Day 2 priorities.",
      items: [
        "Day 1 decisions read back",
        "SUCCESS CHECKPOINT: Validate against success definition from Day 1 Session 1",
        "Confirm Day 2 agenda and any adjustments",
        "Energy check - what needs more attention today",
      ],
      output: "Team aligned. Day 2 priorities confirmed. Success criteria front of mind.",
      presFile: null,
      facFile: null,
      captureTab: null,
      preRead: null,
    },
    {
      num: 3, time: "9:15 - 10:15", type: "working",
      block: "Agentic Day Planner - Pilot Planning Session",
      sessionNum: "",
      owner: "Dave Smith / Vanessa Fucciani",
      purpose: "Turn the confirmed flagship pilot into an executable plan.",
      items: [
        "Data readiness audit results: is recruiter or rep data cleaner in Bullhorn today?",
        "Pilot cohort selection: name the 5-10 users in the pilot group",
        "Signal weighting input: what should the agent prioritize?",
        "Outlook delivery design: walk through a mock morning plan",
        "Build timeline: what must be done in Phase 3 to enable Dec 2026 launch?",
        "Success criteria locked: open rate, action rate, activity impact, user feedback threshold",
        "Data readiness gate review: which of the 5 gate criteria are met today?",
        "Owner assignments: who owns the build, the pilot, and the 30-day review?",
      ],
      output: "Pilot cohort named. Build timeline confirmed. Success criteria locked. Data readiness gaps assigned.",
      presFile: "Agentic_Day_Planner_Pilot_Planning_Dave Feedback_Draft 1.pptx",
      facFile: "Agentic_Day_Planner_Facilitator_Guide.docx",
      captureTab: "Pilot Planning Capture",
      preRead: ["Review Agentic Day Planner tab before attending"],
    },
    {
      num: 4, time: "10:15 - 10:30", type: "break",
      block: "Break", owner: "", purpose: "", items: [], output: "", captureTab: null,
    },
    {
      num: 5, time: "10:30 - 11:45", type: "working",
      block: "AI Governance and Guardrails",
      sessionNum: "",
      owner: "Dan Frasco / Rachel Hopper",
      purpose: "Define the rules. Ratify the AI Charter. Assign guardrail owners. Rachel Hopper leads change management.",
      items: [
        "The non-negotiable: AI reads the Semantic Layer only - never raw systems",
        "Role-based access at the AI query layer - what each role can and cannot see",
        "Every AI output traces to an auditable source with full lineage",
        "Client-facing AI outputs require human review gate",
        "Acceptable use policy for AI-generated reports",
        "CHANGE MANAGEMENT: Who is affected by AI deployment and how",
        "CHANGE MANAGEMENT: Adoption strategy for recruiter and rep populations",
        "CHANGE MANAGEMENT: Communication plan for Agentic Day Planner pilot - Rachel Hopper owner",
        "Data classification requirements before AI layer launches",
        "Bias mitigation and transparency standards",
      ],
      output: "Written AI governance guardrails. Change management plan with Rachel as owner. Signed off by exec sponsors.",
      presFile: "AI_Governance_Guardrails.pptx",
      facFile: "AI_Governance_Facilitator_Guide.docx",
      captureTab: "Governance Capture",
      preRead: ["Review AI Strategy tab before attending"],
    },
    {
      num: 6, time: "11:45 - 12:15", type: "break",
      block: "Lunch", owner: "", purpose: "", items: [], output: "", captureTab: null,
    },
    {
      num: 7, time: "12:15 - 1:30", type: "working",
      block: "Story Framework Working Session",
      sessionNum: "",
      owner: "Vanessa Fucciani / Group",
      purpose: "Confirm the 9 story frameworks. Assign owners. Define Phase 2 build sequence.",
      items: [
        "FIRST: Ratify metric conflict resolution rules (Mike Dansby). NetSuite wins on financials: GAAP revenue, gross margin, EBITDA, net income, cash flow. Bullhorn wins on ops: spread, commissions, billable resources, all operational metrics. Engineering Services pro-forma handled in Bullhorn. Spread/commission moves from weekly billing spreadsheet to hardened semantic layer process. Confirm or amend before story work begins.",
        "Review all 9 story frameworks: Individual Player, Candidate, Client, Enterprise, Site, Vertical, Industry, Company, Financial",
        "Confirm Phase 2 priority order: Individual Player, Candidate, Client, Financial",
        "Assign business owner and BI build owner for each framework",
        "Map data gaps per story - what is missing, what needs to be fixed first",
        "Define what done looks like for each Phase 2 story",
      ],
      output: "9 story frameworks confirmed. Phase 2 stories owned. Data gap map per story.",
      presFile: "Story_Framework_Clean_v1.pptx",
      facFile: "Story_Framework_Facilitator_Guide.docx",
      captureTab: "Story Capture",
      preRead: ["Open Story Capture tab - you will assign owners live during this session"],
    },
    {
      num: 8, time: "1:30 - 1:45", type: "break",
      block: "Break", owner: "", purpose: "", items: [], output: "", captureTab: null,
    },
    {
      num: 9, time: "1:45 - 2:30", type: "working",
      block: "Execution Planning and Ownership",
      sessionNum: "",
      owner: "Dan Frasco / Rachel Hopper",
      purpose: "Assign everything that came out of the last 2 days. No decision leaves without an owner.",
      items: [
        "Phase 2 kickoff date and milestone calendar",
        "AI phase gate criteria - what must be true before Phase 4 launches",
        "Owner assignments for every open workstream including Agentic Day Planner pilot",
        "Bandwidth and capacity - honest assessment of team constraints",
        "Escalation path for blockers",
        "Monthly executive update cadence: format, frequency, audience",
      ],
      output: "Complete ownership model. Milestone dates set. Escalation path documented.",
      presFile: "Execution_Planning_Ownership.pptx",
      facFile: "Execution_Planning_Facilitator_Guide.docx",
      captureTab: "Execution Capture",
      preRead: ["Open Execution Capture tab - you will assign owners live during this session"],
    },
    {
      num: 10, time: "2:30 - 3:00", type: "exec",
      block: "Summit Close, Commitments and Final Success Checkpoint",
      sessionNum: "",
      owner: "Dave Smith / Sab Guerriero / Dan Frasco",
      purpose: "Lock every decision. Final success checkpoint. End with momentum.",
      items: [
        "All decisions made across 2 days - read them back aloud",
        "FINAL SUCCESS CHECKPOINT: Did we achieve what we defined this morning?",
        "Every open item has an owner and a due date",
        "Next milestone: Phase 2 kickoff",
        "Agentic Day Planner pilot cohort named and pilot owner confirmed",
        "30-day follow up date confirmed",
        "Executive commitment to the platform build",
      ],
      output: "Complete decision log. Action item register. Success validated. Every person leaves with their assignments.",
      presFile: "Summit_Close_Commitments.pptx",
      facFile: "Summit_Close_Facilitator_Guide.docx",
      captureTab: "Summit Close Capture",
    },
    {
      num: "S15",
      type: "WORKING SESSION",
      time: "Admin Only",
      name: "Acceleration Planning",
      owner: "Dan Frasco",
      owners: [{ name: "Dan Frasco", role: "CIO" }],
      captureTab: "Acceleration Capture",
    },
  ],
};
export const DRAFT_MATERIALS_BASE = "https://triplecc.sharepoint.com/sites/OperationsITHub/Data%20and%20Intelligence%20Platform/June%202026%20AI%20Summit/AI%20Summit%20Materials/";

// Project metadata
export const PROJECT_META = {
  name: "TCC Data Platform",
  client: "Triple Crown Consulting",
  subtitle: "AI Summit: June 25-26, TCC Austin | $200M to $500M",
  domain: "https://datamvps.tripleco.com",
  sharepoint: {
    tenant: "triplecc.sharepoint.com",
    site: "OperationsITHub",
    driveId: "b!lQhaegUp90W6doNIX0gCPf75Qk46uGpGghXwC-9N3hPGlFCOtv4NT4o7JmQUKRJ-",
    statusFileId: "0137WX5PTNMHXORHXRHFDYOC3D2HD7ZWJ2",
    summitDataFolder: "TCC Summit Data",
  },
  team: {
    "Dan Frasco":       "dan@tripleco.com",
    "Vanessa Fucciani": "vanessa@tripleco.com",
    "Martin Lopez":     "martin@tripleco.com",
    "Mike Dansby":      "mdansby@tripleco.com",
    "Rachel Hopper":    "rachel@tripleco.com",
    "Dave Smith":       "dave@tripleco.com",
    "Sab Guerriero":    "sab@tripleco.com",
  },
  publicTabs: ["Status"],
  azure: {
    clientId: "1aabd350-0bb8-41d2-b07d-51e5c8f17cb7",
    tenantId: "3486c605-47d5-406f-8894-4e21c75dd48f",
  },
};

// Project permission configuration
// Controls tab access by AD group and user overrides
// Resolution order: userOverrides > AD group > defaultLevel
export const PROJECT_PERMISSIONS = {
  projectId: "data-platform",
  clientId: "tcc",
  groups: {
    // TCC global - access across all TCC projects
    "TrapperKeeper-TCC-Admin":  "admin",
    "TrapperKeeper-TCC-Modify": "modify",
    "TrapperKeeper-TCC-Write":  "write",
    "TrapperKeeper-TCC-View":   "view",
    // TCC project-specific - this project only
    "TrapperKeeper-TCC-DataPlatform-Admin":  "admin",
    "TrapperKeeper-TCC-DataPlatform-Modify": "modify",
    "TrapperKeeper-TCC-DataPlatform-Write":  "write",
    "TrapperKeeper-TCC-DataPlatform-View":   "view",
  },
  userOverrides: {
    "dan@tripleco.com":     "admin",
    "vanessa@tripleco.com": "write",
    "martin@tripleco.com":  "write",
    "mdansby@tripleco.com": "write",
    "rachel@tripleco.com":  "write",
    "dave@tripleco.com":    "modify",
    "sab@tripleco.com":     "modify",
  },
  tabOverrides: {
    "Status":                       { minLevel: "view",   public: true },
    "Admin":                        { minLevel: "admin",  public: false },
    "New Hire Onboarding Tracker":  { minLevel: "view",   public: false },
    "AI Business Case":             { minLevel: "view",   public: false },
    "AI Charter":                   { minLevel: "view",   public: false },
    "AI Strategy":                  { minLevel: "view",   public: false },
    "Architecture":                 { minLevel: "view",   public: false },
    "Metrics":                      { minLevel: "view",   public: false },
    "Ownership":                    { minLevel: "view",   public: false },
    "Data Audit":                   { minLevel: "view",   public: false },
    "AI Summit":                    { minLevel: "view",   public: false },
    "Agentic Day Planner":          { minLevel: "view",   public: false },
    "Readiness Capture":            { minLevel: "write",  public: false },
    "Use Case Capture":             { minLevel: "write",  public: false },
    "Data Platform Capture":        { minLevel: "write",  public: false },
    "Pilot Planning Capture":       { minLevel: "write",  public: false },
    "Governance Capture":           { minLevel: "write",  public: false },
    "Story Capture":                { minLevel: "write",  public: false },
    "Execution Capture":            { minLevel: "write",  public: false },
    "Current State Capture":        { minLevel: "write",  public: false },
    "Day 1 Wrap Capture":           { minLevel: "write",  public: false },
    "Summit Close Capture":         { minLevel: "write",  public: false },
  },
  defaultLevel: "write",
};
